"use client";

/**
 * Smart ad rotation + viewability for client ad slots.
 *
 * Behaviour (matches docs/sucom-ads/SUCOM_ADS_SPEC.md §14):
 *  - Rotates the current creative every `intervalMs` (default 15s) ONLY while the
 *    slot is ≥50% visible AND the tab is in the foreground. Pauses otherwise and
 *    resumes (it never "catches up" missed cycles).
 *  - Counts a *viewable impression* only after the creative has been ≥50% visible
 *    for ≥1s continuously (IAB/MRC), and at most once per displayed creative cycle.
 *  - Caps total rotations per mount (`maxRotations`) so we never inflate inventory
 *    or fatigue the user.
 *  - The visual swap (cross-fade) is done by the consumer via CSS on `current.id`,
 *    so there is no layout reflow → CLS stays ~0.
 *
 * When `config` is null or there is a single item, no rotation happens but a
 * single viewable impression is still tracked.
 */
import { useCallback, useEffect, useRef, useState } from "react";

export interface RotationConfig {
  enabled: boolean;
  intervalMs: number;
  maxRotations: number;
}

const VIEW_RATIO = 0.5;
const DWELL_MS = 1000;

interface Trackable {
  id: string;
  campaignId?: string | null;
  creativeId?: string | null;
  placement?: string | null;
}

function trackViewable(item: Trackable) {
  const isInternalCampaign =
    item.campaignId &&
    !item.campaignId.startsWith("legacy:") &&
    !item.campaignId.startsWith("ext:");
  const url = isInternalCampaign ? "/api/ads/viewable" : "/api/ads/track";
  const payload = JSON.stringify(
    isInternalCampaign
      ? {
          campaignId: item.campaignId,
          creativeId: item.creativeId,
          placement: item.placement,
          event: "impression",
        }
      : { id: item.id, event: "impression", placement: item.placement },
  );
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      navigator.sendBeacon(
        url,
        new Blob([payload], { type: "application/json" }),
      );
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  void fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});
}

export function useAdRotation<T extends Trackable>(
  items: T[],
  config: RotationConfig | null,
) {
  const [index, setIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const visibleRef = useRef(false);
  const rotationsRef = useRef(0);
  // Dedup viewable impressions per (creativeId + cycle index).
  const countedRef = useRef<Set<string>>(new Set());

  const safeIndex = items.length > 0 ? index % items.length : 0;
  const current = items[safeIndex];

  const markViewable = useCallback(
    (item: T) => {
      const key = `${item.creativeId ?? item.id}:${rotationsRef.current}`;
      if (countedRef.current.has(key)) return;
      countedRef.current.add(key);
      trackViewable(item);
    },
    [],
  );

  // Viewability: observe the container; fire a viewable impression after the
  // creative has been ≥50% visible for ≥1s with the tab in the foreground.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !current) return;
    let dwell: ReturnType<typeof setTimeout> | null = null;
    const clearDwell = () => {
      if (dwell) {
        clearTimeout(dwell);
        dwell = null;
      }
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        const vis = entry.isIntersecting && entry.intersectionRatio >= VIEW_RATIO;
        visibleRef.current = vis;
        if (vis && !document.hidden) {
          clearDwell();
          dwell = setTimeout(() => {
            if (visibleRef.current && !document.hidden) markViewable(current);
          }, DWELL_MS);
        } else {
          clearDwell();
        }
      },
      { threshold: [0, VIEW_RATIO, 1] },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      clearDwell();
    };
  }, [current, markViewable]);

  // Rotation: tick every intervalMs, but only advance when visible + foreground
  // and under the rotation cap. Skipped ticks simply do nothing (no catch-up).
  useEffect(() => {
    if (!config?.enabled || items.length < 2) return;
    const id = setInterval(() => {
      if (!visibleRef.current || document.hidden) return;
      if (rotationsRef.current >= config.maxRotations) return;
      rotationsRef.current += 1;
      setIndex((i) => (i + 1) % items.length);
    }, config.intervalMs);
    return () => clearInterval(id);
  }, [config?.enabled, config?.intervalMs, config?.maxRotations, items.length]);

  return { containerRef, current, index: safeIndex };
}
